import { DOCUMENT } from '@angular/common';
import { APP_INITIALIZER, FactoryProvider, Injectable, inject } from '@angular/core';

import { Observable, ReplaySubject, Subject, from, lastValueFrom, race, take, timer } from 'rxjs';

import { DevBuild, EnvironmentService, FeatureToggleService, Logger, PlatformService } from '@hdms/shared/util-core';

import { FT_NGC_TOKEN_SERVICE_WORKER } from '../toggles';
import { UserContextService } from '../user';

import { registerServiceWorker } from './service-worker.helper';
import { PostMessageInputData, PostMessageOutputData, PostMessageType } from './service-worker.types';

const WORKER_INIT_TIMEOUT = 10000;

/**
 * This service registers a service-worker on the global website scope `/`, so it's the one and only service-worker
 * not being limited to sub-routes of Hilti website. It's not possible to register any other service-worker for the
 * same scope without unregistering the current one. So no other service-worker with same scope should be added.
 *
 * The generic service implementation only cares for worker registration and establishing the communication.
 * It acts as a proxy, which allows to extend service-worker functionality by
 * 1.) adding a new worker class to the service-worker, running specific logic.
 * 2.) adding a new service to the app, which injects this service to communicate with its related worker class.
 */
@Injectable({
  providedIn: 'root'
})
export class ServiceWorkerService<
  INPUT = PostMessageInputData,
  OUTPUT extends PostMessageOutputData = PostMessageOutputData
> {
  private readonly env = inject(EnvironmentService);
  private readonly userContext = inject(UserContextService);
  private readonly ftService = inject(FeatureToggleService);
  private readonly isBrowser = inject(PlatformService).isBrowser;
  private readonly scope = `${inject(DOCUMENT).location.origin}/`;

  private readonly readySubject = new ReplaySubject<void>(1);
  private readonly messageSubject = new Subject<OUTPUT>();

  private readonly serviceWorkerContainer = this.getServiceWorkerContainer();
  private serviceWorker: globalThis.ServiceWorker;

  private initData: Omit<INPUT, 'type'>;

  get ready$(): Observable<void> {
    return this.readySubject.pipe(take(1));
  }

  get message$(): Observable<OUTPUT> {
    return this.messageSubject.asObservable();
  }

  /**
   * Init the service in browser by using a 10 sec timeout as race condition in order
   * to unblock bootstrapping in some rare cases, when registering service worker
   * might hang the app while executing app initializer.
   */
  public init(): Observable<unknown> {
    return this.isBrowser ? race([from(this.initialize()), timer(WORKER_INIT_TIMEOUT)]) : null;
  }

  /**
   * Collect data from other services using this service proxy
   * for providing initial data on service-worker init.
   * @param data
   */
  public addInitData(data: Omit<INPUT, 'type'>): void {
    this.initData = { ...this.initData, ...data };
  }

  public postMessage(data: PostMessageInputData | INPUT): void {
    this.serviceWorker?.postMessage(data);
  }

  /**
   * Initialize and register the service-worker.
   * In any case, emit `ready$` after initialization completed.
   *
   * Due to a bug in Safari (no message handshake after redirect from client-oauth/cloudsso),
   * in order to fix it SW has to be un/registered each time logged-in state changes!
   * This unfortunately doesn't allow to persist the SW once it has been installed.
   * TODO: check future Safari releases (v17+) if issue still occurs!
   */
  private async initialize(): Promise<unknown> | undefined {
    if (!this.serviceWorker && this.serviceWorkerContainer) {
      try {
        if (
          this.userContext.isLoggedIn &&
          (this.ftService.getToggleById(FT_NGC_TOKEN_SERVICE_WORKER) || this.env.devBuild === DevBuild.NGC)
        ) {
          await registerServiceWorker(this.scope);
          return this.initCommunication();
        } else {
          return this.unregister();
        }
      } catch (e) {
        Logger.error('ServiceWorker registration failed:', e);
      }
    }

    this.readySubject.next();
  }

  private getServiceWorkerContainer(): ServiceWorkerContainer {
    return this.env.isStandalone && this.isBrowser && 'serviceWorker' in navigator && navigator.serviceWorker;
  }

  private async unregister(): Promise<unknown> {
    const registration = await this.serviceWorkerContainer.getRegistration(this.scope);
    this.readySubject.next();
    return registration?.unregister();
  }

  /**
   * Initialize the communication with the service-worker and pass on
   * collected init-data to all worker classes added to the service-worker.
   * @private
   */
  private async initCommunication(): Promise<void> {
    const { active } = await this.serviceWorkerContainer.ready;
    this.serviceWorker = active;

    this.listenToMessages();

    this.postMessage({
      type: PostMessageType.INIT,
      ...this.initData
    });

    return lastValueFrom(this.ready$);
  }

  /**
   * Listen to messages from service-worker.
   * On receiving `ready` message, `ready$` observable will be triggered.
   * For other messages, data will be emitted via `message$` observable.
   * @private
   */
  private listenToMessages(): void {
    this.serviceWorkerContainer.addEventListener('message', ({ data = {} as OUTPUT }: MessageEvent<OUTPUT>) => {
      if (data.ready) {
        this.readySubject.next();
      } else {
        this.messageSubject.next(data);
      }
    });
  }
}

export const SERVICE_WORKER_PROVIDER: FactoryProvider = {
  provide: APP_INITIALIZER,
  useFactory: (serviceWorker: ServiceWorkerService) => () => serviceWorker.init(),
  deps: [ServiceWorkerService],
  multi: true
};
